import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { PlusCircle, MapPin, History, Calendar as CalendarIcon, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X } from 'lucide-react';
import L from 'leaflet';

// Componentes da página
import TradeCard from '../common/cards/TradeCard';
import Pagination from '../common/Pagination'; // <-- Importação do novo componente
import SearchInput from '../common/SearchInput';

// Hooks e constantes
import { useTrades } from '../../hooks/useTrades';
import { TRADE_STATUSES } from '../../constants/appConstants';
import { getMapTileAttribution, getMapTileUrl } from '../../config/mapTiles';

// --- FUNÇÃO AUXILIAR PARA NORMALIZAÇÃO DE TEXTO ---
const normalizeText = (text) => {
    if (!text || typeof text !== 'string') return '';
    return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
};



// --- COMPONENTE DE EXIBIÇÃO DE STATUS (TOTALIZADORES) ---
// ⚡ Bolt: Wrapped in React.memo() to prevent re-renders when parent TradesPage state (like searchTerm) changes.
// Only re-renders when the actual status data counts change.
const TradesStatusDisplay = React.memo(({ data }) => {
    if (!data || data.length === 0) {
        return null;
    }
    return (
        <div className="flex gap-3 overflow-x-auto pb-3 lg:grid lg:grid-cols-5 lg:gap-4">
            {data.map((item, index) => (
                <div 
                    key={index} 
                    className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow-md border-t-4 flex-shrink-0 w-36 lg:w-auto" 
                    style={{ borderColor: item.color || '#ccc' }}
                >
                    <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{item.count}</p>
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400 truncate">{item.name}</p>
                </div>
            ))}
        </div>
    );
});

// --- COMPONENTE DO MAPA COM OVERLAY DE INTERAÇÃO ---
// ⚡ Bolt: Wrapped in React.memo() to prevent expensive map re-renders during text input or status filtering.
// The map only needs to re-render when the filtered trades list or dark mode setting changes.
const TradesMap = React.memo(({ trades, onMarkerClick, darkMapEnabled }) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const tileLayerRef = useRef(null);
    const markersRef = useRef([]);
    const [isMapActive, setIsMapActive] = useState(false);

    useEffect(() => {
        if (mapContainerRef.current && !mapRef.current) {
            mapRef.current = L.map(mapContainerRef.current, {
                center: [-14.235, -51.925],
                zoom: 4,
                zoomControl: false,
                dragging: !L.Browser.mobile,
                tap: !L.Browser.mobile,
                touchZoom: false,
            });
            L.control.zoom({ position: 'bottomright' }).addTo(mapRef.current);
        }
        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (mapRef.current && L.Browser.mobile) {
            if (isMapActive) {
                mapRef.current.dragging.enable();
                mapRef.current.touchZoom.enable();
            }
        }
    }, [isMapActive]);

    useEffect(() => {
        if (!mapRef.current) return;
        const tileUrl = getMapTileUrl(darkMapEnabled);
        if (tileLayerRef.current) {
            tileLayerRef.current.setUrl(tileUrl);
        } else {
            tileLayerRef.current = L.tileLayer(tileUrl, { attribution: getMapTileAttribution(darkMapEnabled) }).addTo(mapRef.current);
        }
    }, [darkMapEnabled]);

    useEffect(() => {
        if (!mapRef.current) return;
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
        const tradesWithCoords = trades.filter(t => t.coordinates && t.coordinates.lat && t.coordinates.lng);

        if (tradesWithCoords.length > 0) {
            const newMarkers = tradesWithCoords.map(trade => {
                let markerColor = '#3b82f6';
                if (trade.isNoNetwork) {
                    markerColor = '#D1D5DB';
                } else if (trade.isCritical) {
                    markerColor = '#F97316';
                }

                const marker = L.marker([trade.coordinates.lat, trade.coordinates.lng], {
                    icon: L.divIcon({
                        html: `<svg viewBox="0 0 24 24" width="30" height="30" fill="${markerColor}" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" stroke="white" stroke-width="1"/></svg>`,
                        className: 'bg-transparent border-0', iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -30]
                    })
                });
                marker.on('click', () => onMarkerClick && onMarkerClick(trade));
                marker.bindPopup(`<b>NS: ${trade.serviceNote}</b><br />${trade.address || 'Endereço não disponível'}`);
                return marker;
            });
            const group = new L.featureGroup(newMarkers).addTo(mapRef.current);
            mapRef.current.fitBounds(group.getBounds().pad(0.2));
            markersRef.current = newMarkers;
        } else {
            mapRef.current.setView([-14.235, -51.925], 4);
        }
    }, [trades, onMarkerClick]);

    return (
        <div className="relative rounded-lg shadow-md overflow-hidden">
            <div ref={mapContainerRef} className="h-64 md:h-80 w-full z-0" aria-label="Mapa de localização das trocas" />
            {!isMapActive && L.Browser.mobile && (
                <div 
                    className="absolute inset-0 bg-black/30 backdrop-blur-sm flex flex-col items-center justify-center z-10 cursor-pointer"
                    onClick={() => setIsMapActive(true)}
                >
                    <MapPin className="h-8 w-8 text-white mb-2" />
                    <p className="text-white font-semibold">Toque para interagir com o mapa</p>
                </div>
            )}
        </div>
    );
});


// --- COMPONENTE DE CONTROLES (FILTROS E BOTÕES) ---
// ⚡ Bolt: Wrapped in React.memo() to prevent unnecessary re-renders of the entire control section
// when other parts of TradesPage update (e.g. paginatedTrades).
export const TradesControls = React.memo(({
    searchTerm,
    onSearchChange,
    statusFilter,
    onStatusChange,
    onNewClick,
    statuses,
    userRole,
    showOldTrades,
    onToggleShowOld,
    selectedDate,
    onDateChange,
    onClearDate,
    onPrevDate,
    onNextDate,
}) => {
    const canCreate = userRole === 'administrador' || userRole === 'tecnico';

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
    };

    return (
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
            
            <div className="w-full md:flex-1">
                <SearchInput
                    placeholder="Buscar por nota, endereço, bairro..."
                    value={searchTerm}
                    onChange={onSearchChange}
                    className="w-full"
                    aria-label="Buscar trocas"
                />
            </div>
            
            <div className="flex flex-wrap md:flex-nowrap items-center gap-3 w-full md:w-auto">
                    <div className="relative flex-grow sm:flex-grow-0 sm:w-48">
                     <select
                        value={statusFilter}
                        onChange={onStatusChange}
                        aria-label="Filtrar por status"
                        className="w-full appearance-none px-4 py-2 pr-10 h-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    >
                        <option value="Todos">Todos Status</option>
                        {statuses.map(status => (
                            <option key={status.value} value={status.value}>{status.label}</option>
                        ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 dark:text-slate-400">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" aria-hidden="true"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                    </div>
                </div>

                {selectedDate ? (
                    <div className="flex items-center h-10 bg-slate-100 dark:bg-slate-700 rounded-lg border border-slate-300 dark:border-slate-600 overflow-hidden">
                        <button
                            onClick={onPrevDate}
                            className="p-2 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors h-full flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            aria-label="Dia anterior"
                            title="Dia anterior"
                        >
                            <ChevronLeft className="h-5 w-5 text-slate-700 dark:text-slate-200" aria-hidden="true" />
                        </button>
                        <span className="px-3 font-medium text-slate-800 dark:text-slate-100 min-w-[100px] text-center whitespace-nowrap">
                            {formatDateForDisplay(selectedDate)}
                        </span>
                        <button
                            onClick={onNextDate}
                            className="p-2 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors h-full flex items-center justify-center border-r border-slate-300 dark:border-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            aria-label="Próximo dia"
                            title="Próximo dia"
                        >
                            <ChevronRight className="h-5 w-5 text-slate-700 dark:text-slate-200" aria-hidden="true" />
                        </button>
                        <button
                            onClick={onClearDate}
                            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors h-full flex items-center justify-center group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                            aria-label="Limpar data"
                            title="Limpar data"
                        >
                            <X className="h-5 w-5 text-slate-500 group-hover:text-red-600 dark:text-slate-400 dark:group-hover:text-red-400" aria-hidden="true" />
                        </button>
                    </div>
                ) : (
                    <div className="relative flex-shrink-0 h-10">
                        <input
                            type="date"
                            value={selectedDate || ''}
                            onChange={onDateChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 peer"
                            aria-label="Selecionar data"
                            title="Selecionar data"
                        />
                        <button
                            className="p-2 h-10 w-10 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors border border-slate-300 dark:border-slate-600 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            aria-label="Abrir calendário"
                            title="Filtrar por data"
                            tabIndex={-1}
                        >
                            <CalendarIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </div>
                )}

                <div className="flex-shrink-0 h-10">
                    <button
                        onClick={onToggleShowOld}
                        disabled={!!selectedDate}
                        title={showOldTrades ? 'Ocultar trocas antigas' : 'Exibir todas as trocas'}
                        aria-label={showOldTrades ? 'Ocultar trocas antigas' : 'Exibir todas as trocas'}
                        aria-pressed={showOldTrades}
                        className={`p-2 h-full rounded-lg text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${selectedDate ? 'bg-slate-300 dark:bg-slate-600 cursor-not-allowed opacity-50' : showOldTrades ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-500 hover:bg-slate-600'}`}
                    >
                        <History className="h-5 w-5" aria-hidden="true" />
                    </button>
                </div>

                {canCreate && (
                     <div className="flex-1 sm:flex-none h-10">
                        <button
                            onClick={onNewClick}
                            className="h-full w-full flex items-center justify-center whitespace-nowrap bg-blue-600 text-white font-bold px-4 rounded-lg hover:bg-blue-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        >
                            <PlusCircle size={18} className="mr-2" aria-hidden="true" />
                            Nova Troca
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
});


// --- FUNÇÕES HELPER ---
const parseDate = (dateStr) => {
    if (!dateStr) return null;
    // Divide a string da data 'DD/MM/AAAA' em partes
    const parts = dateStr.split('/');
    if (parts.length === 3) {
        // Mapeia as partes para números
        const [day, month, year] = parts.map(Number);
        // Verifica se as partes são números válidos e cria o objeto Date
        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
             // O mês no construtor Date é baseado em zero (0-11), então subtraímos 1
             return new Date(year, month - 1, day);
        }
    }
    // Fallback para outros formatos de data que o construtor Date possa entender
    const date = new Date(dateStr);
    // Retorna a data se for válida, caso contrário, retorna nulo
    return isNaN(date.getTime()) ? null : date;
};

const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
};

const isTomorrow = (date) => {
    if (!date) return false;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.getDate() === tomorrow.getDate() &&
           date.getMonth() === tomorrow.getMonth() &&
               date.getFullYear() === tomorrow.getFullYear();
};

const isAfterTomorrow = (date) => {
    if (!date) return false;
    const afterTomorrow = new Date();
    afterTomorrow.setDate(afterTomorrow.getDate() + 2);
    return date.getDate() === afterTomorrow.getDate() &&
           date.getMonth() === afterTomorrow.getMonth() &&
           date.getFullYear() === afterTomorrow.getFullYear();
};

const getTradeStatus = (trade, tradeDate) => {
    if (trade.isCanceled) return 'cancelada';
    if (trade.isNoNetwork) return 'sem_rede';

    const dateToCheck = tradeDate !== undefined ? tradeDate : parseDate(trade.servicePeriodStart);
    if (!dateToCheck) return null;

    if (isToday(dateToCheck)) return 'hoje';
    if (isTomorrow(dateToCheck)) return 'amanha';
    if (isAfterTomorrow(dateToCheck)) return 'proximos';

    return 'antiga';
};


/**
 * @description Componente principal da página/guia de Trocas.
 */
const TradesPage = ({
    db,
    user,
    userData,
    settings,
    theme,
    onOpenTradeModal,
    onOpenTradeShareModal,
    trades: propTrades,
    loading: propLoading,
    searchTerm: propSearchTerm,
    onSearchChange: propOnSearchChange
}) => {
    const internalResult = useTrades(db, !propTrades ? user : null);
    const trades = propTrades || internalResult.trades;
    const loading = propLoading !== undefined ? propLoading : internalResult.loading;

    const [internalSearchTerm, setInternalSearchTerm] = useState('');
    const searchTerm = propSearchTerm !== undefined ? propSearchTerm : internalSearchTerm;
    const setSearchTerm = propOnSearchChange || setInternalSearchTerm;

    const [statusFilter, setStatusFilter] = useState('Todos');
    const [showOldTrades, setShowOldTrades] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null); // formato 'YYYY-MM-DD'
    const [currentPage, setCurrentPage] = useState(1);
    const [isMapExpanded, setIsMapExpanded] = useState(false);
    const TRADES_PER_PAGE = 15; // Definindo o número de trocas por página

    const isMapDark = settings?.darkMapEnabled && theme === 'dark';


    const preparedTrades = useMemo(() => {
        const normCache = new Map();
        const getNormalized = (text) => {
            if (!text || typeof text !== 'string') return '';
            if (normCache.has(text)) return normCache.get(text);
            const normalized = text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            normCache.set(text, normalized);
            return normalized;
        };

        return trades.map(trade => {
            // ⚡ Bolt: Pre-calculate date object and timestamp to avoid O(N log N) new Date() allocations in sorting
            const dateObj = parseDate(trade.servicePeriodStart);
            const dateTimestamp = dateObj ? dateObj.getTime() : null;

            // ⚡ Bolt: Pre-calculate trade status here to avoid parseDate and Date object creation on every search keystroke
            const computedStatus = getTradeStatus(trade, dateObj);

            return {
                original: trade,
                dateObj,
                dateTimestamp,
                computedStatus,
                searchString: `
                    ${getNormalized(trade.serviceNote)} |||
                    ${getNormalized(trade.address)} |||
                    ${getNormalized(trade.neighborhood)} |||
                    ${getNormalized(trade.city)} |||
                    ${getNormalized(trade.ftthSector)}
                `.toLowerCase()
            };
        });
    }, [trades]);

    const filteredTrades = useMemo(() => {
        const normalizedSearch = normalizeText(searchTerm);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayTime = today.getTime();

        return preparedTrades
            .filter(({ computedStatus, searchString, dateTimestamp, dateObj }) => {
                // Filtro por data selecionada:
                if (selectedDate) {
                    if (!dateObj) return false; // Se tiver data selecionada, e a troca não tiver data, não exibe

                    const [year, month, day] = selectedDate.split('-').map(Number);
                    if (dateObj.getFullYear() !== year || dateObj.getMonth() !== month - 1 || dateObj.getDate() !== day) {
                        return false;
                    }
                } else if (!showOldTrades) {
                    // Mantém a lógica de filtro de trocas antigas se não houver data selecionada
                    if (dateTimestamp !== null && dateTimestamp < todayTime) {
                        return false;
                    }
                }

                const matchesStatus = statusFilter === 'Todos' ? true : computedStatus === statusFilter;
                if (!matchesStatus) return false;

                if (!normalizedSearch) return true;
                return searchString.includes(normalizedSearch);
            })
            .sort((aObj, bObj) => {
                const timeA = aObj.dateTimestamp;
                const timeB = bObj.dateTimestamp;

                // Trata casos onde as datas podem ser nulas ou inválidas
                if (timeA === null && timeB === null) return 0; // Se ambas são inválidas, considera-as iguais
                if (timeA === null) return 1;  // Coloca datas nulas/inválidas no final da lista
                if (timeB === null) return -1; // Coloca datas nulas/inválidas no final da lista
                
                // Mantém a lógica de ordenação ascendente/descendente
                if (showOldTrades) {
                    // Ordena as trocas antigas da mais recente para a mais antiga (descendente)
                    return timeB - timeA;
                }
                
                // Ordena as trocas futuras da mais próxima para a mais distante (ascendente)
                return timeA - timeB;
            })
            .map(item => item.original);
    }, [preparedTrades, searchTerm, statusFilter, showOldTrades, selectedDate]);

    const totalPages = Math.ceil(filteredTrades.length / TRADES_PER_PAGE);
    const paginatedTrades = useMemo(() => {
        const startIndex = (currentPage - 1) * TRADES_PER_PAGE;
        const endIndex = startIndex + TRADES_PER_PAGE;
        return filteredTrades.slice(startIndex, endIndex);
    }, [currentPage, filteredTrades]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter, showOldTrades, selectedDate]);


    const tradesStatusData = useMemo(() => {
        const statusesToDisplay = [
            { key: 'hoje', label: 'Hoje', color: '#ef4444' },
            { key: 'amanha', label: 'Amanhã', color: '#3b82f6' },
            { key: 'proximos', label: 'Depois de amanhã', color: '#eab308' },
            { key: 'sem_rede', label: 'Sem Rede', color: '#f97316' },
            { key: 'cancelada', label: 'Canceladas', color: '#6b7280' },
        ];

        const counts = { hoje: 0, amanha: 0, proximos: 0, cancelada: 0, sem_rede: 0 };

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Zera a hora para comparar apenas a data
        const todayTime = today.getTime();

        preparedTrades.forEach(({ original: trade, dateTimestamp, computedStatus }) => {
            const isFutureOrToday = dateTimestamp !== null && dateTimestamp >= todayTime;

            if (trade.isCanceled && isFutureOrToday) {
                counts.cancelada++;
            } else if (trade.isNoNetwork && isFutureOrToday) {
                counts.sem_rede++;
            } else {
                // Conta apenas os status relacionados a datas futuras/presentes
                if (computedStatus === 'hoje' || computedStatus === 'amanha' || computedStatus === 'proximos') {
                    counts[computedStatus]++;
                }
            }
        });

        return statusesToDisplay.map(status => ({
            name: status.label,
            count: counts[status.key],
            color: status.color,
        }));
    }, [preparedTrades]);

    const handleSearchChange = useCallback((e) => setSearchTerm(e.target.value), [setSearchTerm]);
    const handleStatusChange = useCallback((e) => setStatusFilter(e.target.value), []);
    const handleNewClick = useCallback(() => onOpenTradeModal(), [onOpenTradeModal]);
    const handleToggleShowOld = useCallback(() => setShowOldTrades(prev => !prev), []);

    const handleDateChange = useCallback((e) => {
        setSelectedDate(e.target.value);
    }, []);

    const handleClearDate = useCallback(() => {
        setSelectedDate(null);
    }, []);

    const changeDateByDays = useCallback((days) => {
        setSelectedDate(prev => {
            if (!prev) return prev;
            const [year, month, day] = prev.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            date.setDate(date.getDate() + days);

            // Format to YYYY-MM-DD correctly accounting for local timezone issues
            const newYear = date.getFullYear();
            const newMonth = String(date.getMonth() + 1).padStart(2, '0');
            const newDay = String(date.getDate()).padStart(2, '0');

            return `${newYear}-${newMonth}-${newDay}`;
        });
    }, []);

    const handlePrevDate = useCallback(() => changeDateByDays(-1), [changeDateByDays]);
    const handleNextDate = useCallback(() => changeDateByDays(1), [changeDateByDays]);


    if (loading) {
        return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div></div>;
    }

    return (
        <div className="p-4 space-y-6">
            <section className="space-y-6">
                <TradesStatusDisplay data={tradesStatusData} />
                <div>
                    <div
                        className="flex items-center justify-between bg-slate-100 dark:bg-slate-700/50 p-3 rounded-lg cursor-pointer select-none"
                        role="button"
                        tabIndex={0}
                        aria-expanded={isMapExpanded}
                        onClick={() => setIsMapExpanded(prev => !prev)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsMapExpanded(prev => !prev); } }}
                    >
                        <span className="font-medium text-slate-700 dark:text-slate-200">Mapa de Trocas</span>
                        {isMapExpanded ? <ChevronUp size={20} className="text-slate-500 dark:text-slate-400" /> : <ChevronDown size={20} className="text-slate-500 dark:text-slate-400" />}
                    </div>
                    {isMapExpanded && (
                        <div className="mt-3">
                            <TradesMap trades={filteredTrades} darkMapEnabled={isMapDark} onMarkerClick={onOpenTradeModal} />
                        </div>
                    )}
                </div>
            </section>

            <section>
                <TradesControls
                    searchTerm={searchTerm}
                    onSearchChange={handleSearchChange}
                    statusFilter={statusFilter}
                    onStatusChange={handleStatusChange}
                    onNewClick={handleNewClick}
                    statuses={TRADE_STATUSES}
                    userRole={userData?.role}
                    showOldTrades={showOldTrades}
                    onToggleShowOld={handleToggleShowOld}
                    selectedDate={selectedDate}
                    onDateChange={handleDateChange}
                    onClearDate={handleClearDate}
                    onPrevDate={handlePrevDate}
                    onNextDate={handleNextDate}
                />
                <div className="space-y-4">
                    {paginatedTrades.length > 0 ? (
                        paginatedTrades.map(trade => (
                            <TradeCard key={trade.id} trade={trade} onCardClick={onOpenTradeModal} onShareClick={onOpenTradeShareModal} />
                        ))
                    ) : (
                        <p className="text-center text-slate-500 dark:text-slate-400 py-8">
                            Nenhuma troca encontrada com os filtros atuais.
                        </p>
                    )}
                </div>
                
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            </section>
        </div>
    );
};

export default TradesPage;
